import React, { useCallback, useEffect, useMemo, useState } from "react";
import { OrdersFetchAPI } from "../../../api/Orders";
import { reprintPaidOrder } from "../../../utils/PrintPaidOrder";
import AlertSmall from "../../Alerts/AlertSmall";
import "../css/TableTransactions.css";

// Pestaña "Transacciones" del panel de la mesa: ordenes ya cobradas de ESTA mesa dentro de un rango de
// tiempo (GET /table/:id/transactions), con detalle y reimpresion de la factura.
const RANGES = [
  { key: "1h", label: "Última hora", ms: 60 * 60 * 1000 },
  { key: "6h", label: "6 horas", ms: 6 * 60 * 60 * 1000 },
  { key: "24h", label: "24 horas", ms: 24 * 60 * 60 * 1000 },
  { key: "7d", label: "7 días", ms: 7 * 24 * 60 * 60 * 1000 },
  { key: "30d", label: "30 días", ms: 30 * 24 * 60 * 60 * 1000 },
];

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatDateTime = (iso) => {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const day = date.toLocaleDateString("es-PA", { day: "numeric", month: "short" });
  const time = date.toLocaleTimeString("es-PA", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${day}, ${time}`;
};

const sourceLabel = (source) => {
  if (source === "ECOMMERCE_TABLE_PREPAY") return "QR pre-pago";
  if (source === "ECOMMERCE_TABLE") return "QR post-pago";
  return null;
};

// "0" es el default que el backend deja cuando no hubo usuario/mesero.
const cleanName = (value) => (value && value !== "0" && value !== 0 ? String(value) : "");

export default function TableTransactionsTab({ table }) {
  const [rangeKey, setRangeKey] = useState("24h");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [printingId, setPrintingId] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const tableId = table?.id;

  const load = useCallback(async () => {
    if (!tableId) return;
    const range = RANGES.find((item) => item.key === rangeKey) || RANGES[2];
    const to = new Date();
    const from = new Date(to.getTime() - range.ms);

    setLoading(true);
    setError("");
    try {
      const response = await OrdersFetchAPI.getTableTransactions(
        tableId,
        { from: from.toISOString(), to: to.toISOString() },
        localStorage.getItem("x-access-machine-token")
      );
      setOrders(response?.data?.data?.orders || []);
    } catch (err) {
      setOrders([]);
      setError(err?.response?.data?.error || "No se pudieron cargar las transacciones");
    } finally {
      setLoading(false);
    }
  }, [tableId, rangeKey]);

  // Carga al abrir la pestaña, al cambiar de rango o de mesa, y con el boton de refrescar.
  useEffect(() => {
    setExpandedId(null);
    load();
  }, [load, reloadTick]);

  const total = useMemo(() => orders.reduce((sum, order) => sum + Number(order.total || 0), 0), [orders]);

  const handlePrint = async (order) => {
    if (printingId) return;
    setPrintingId(order._id);
    try {
      await reprintPaidOrder(order);
      AlertSmall(`Transacción ${order.transaction_code || order.code} enviada a imprimir`, "success");
    } catch (err) {
      console.error(err);
      AlertSmall(err?.message || "No se pudo imprimir la transacción", "warning");
    } finally {
      setPrintingId(null);
    }
  };

  return (
    <div className="off-x-detail-section">
      <div className="off-x-detail-section-title">
        <i className="bi bi-receipt" /> Transacciones de la mesa
      </div>

      <div className="off-x-tx-ranges" role="tablist" aria-label="Rango de tiempo">
        {RANGES.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`off-x-tx-range ${rangeKey === item.key ? "active" : ""}`}
            onClick={() => setRangeKey(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="off-x-tx-summary">
        <span>
          {loading ? "Cargando..." : `${orders.length} transacci${orders.length === 1 ? "ón" : "ones"}`}
          {!loading && orders.length > 0 ? ` · ${money(total)}` : ""}
        </span>
        <button type="button" className="off-x-tx-refresh" onClick={() => setReloadTick((tick) => tick + 1)} disabled={loading}>
          <i className="bi bi-arrow-clockwise" /> Actualizar
        </button>
      </div>

      {error ? <div className="off-x-tx-error">{error}</div> : null}

      {!loading && !error && orders.length === 0 ? (
        <div className="off-x-empty">No hay transacciones en este rango</div>
      ) : null}

      <div className="off-x-tx-list">
        {orders.map((order) => {
          const expanded = expandedId === order._id;
          const origin = sourceLabel(order.order_source);
          const cashier = cleanName(order.user?.name);
          const waiter = cleanName(order.waiter?.name);
          const payments = Array.isArray(order.paymethods) ? order.paymethods : [];
          const lines = [...(order.groups || []), ...(order.combos || [])];

          return (
            <div key={order._id} className={`off-x-tx-card ${expanded ? "open" : ""}`}>
              <button
                type="button"
                className="off-x-tx-head"
                aria-expanded={expanded}
                onClick={() => setExpandedId(expanded ? null : order._id)}
              >
                <div className="off-x-tx-head-main">
                  <span className="off-x-tx-code">#{order.transaction_code || order.code}</span>
                  <span className="off-x-tx-time">{formatDateTime(order.createdAt)}</span>
                </div>
                <div className="off-x-tx-head-side">
                  <span className="off-x-tx-total">{money(order.total)}</span>
                  <i className={`bi ${expanded ? "bi-chevron-up" : "bi-chevron-down"}`} />
                </div>
              </button>

              <div className="off-x-tx-tags">
                {order.paymenthod ? <span className="off-x-tx-tag">{order.paymenthod}</span> : null}
                {origin ? <span className="off-x-tx-tag is-source">{origin}</span> : null}
                {order.devolution === true ? <span className="off-x-tx-tag is-refund">Devolución</span> : null}
              </div>

              {expanded ? (
                <div className="off-x-tx-body">
                  {lines.length > 0 ? (
                    <ul className="off-x-tx-lines">
                      {lines.map((line, index) => {
                        const segments = Array.isArray(line.segments) ? line.segments : [];
                        return (
                          <li key={`${line.name}-${index}`}>
                            <div className="off-x-tx-line-row">
                              <span className="off-x-tx-line-name">
                                {Number(line.amount || 1)}× {line.name}
                                {line.note ? <em> — {line.note}</em> : null}
                              </span>
                              <span>{money(Number(line.price || 0) * Number(line.amount || 1))}</span>
                            </div>
                            {segments.length > 0 ? (
                              <ul className="off-x-tx-line-segments">
                                {segments.map((seg, segIndex) => {
                                  const segName = seg.name || seg.segment_name || "";
                                  const itemName = seg.item?.name || "";
                                  if (!itemName) return null;
                                  return (
                                    <li key={`${segName}-${segIndex}`}>
                                      {segName ? `${segName}: ` : ""}{itemName}
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}

                  <div className="off-x-tx-totals">
                    <div><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
                    {Number(order.discount) > 0 ? <div><span>Descuento</span><span>-{money(order.discount)}</span></div> : null}
                    <div><span>ITBMS</span><span>{money(order.tax)}</span></div>
                    {Number(order.tips) > 0 ? <div><span>Propina</span><span>{money(order.tips)}</span></div> : null}
                    <div className="is-total"><span>Total</span><span>{money(order.total)}</span></div>
                  </div>

                  {payments.length > 1 ? (
                    <div className="off-x-tx-meta">
                      <strong>Pagos:</strong> {payments.map((payment) => `${payment.name} ${money(payment.total)}`).join(" · ")}
                    </div>
                  ) : null}
                  {order.client_name ? <div className="off-x-tx-meta"><strong>Cliente:</strong> {order.client_name}</div> : null}
                  {cashier ? <div className="off-x-tx-meta"><strong>Cajero:</strong> {cashier}</div> : null}
                  {waiter ? <div className="off-x-tx-meta"><strong>Mesero:</strong> {waiter}</div> : null}

                  <button
                    type="button"
                    className="off-x-tx-print"
                    disabled={printingId === order._id}
                    onClick={() => handlePrint(order)}
                  >
                    <i className="bi bi-printer" /> {printingId === order._id ? "Imprimiendo..." : "Imprimir"}
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
